import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  FileText,
  User,
  Scale,
  DollarSign,
  AlertCircle,
  Eye,
  Clock,
  Search,
  ChevronUp,
  ChevronDown,
  Hash,
  CheckCircle,
  XCircle,
  Archive,
  TrendingUp,
  List,
  MapPin,
  Copy,
  Award,
  ArrowRight,
  FileCheck,
  RefreshCw,
  Ban,
  RotateCcw,
} from "lucide-react";
import ProcessNumber from "../common/ProcessNumber";
import apiService from "../../services/api.js";
import { publicationsDjeService } from "../../services/publicationsDjeService.js";
import { useAuth } from "../../context/AuthContext";
import PartnerLawyerBadge from "../common/PartnerLawyerBadge";
import ConvertedToProcessBadge from "../common/ConvertedToProcessBadge";
import QuickLawyerPartnerModal from "../lawyer-partners/QuickLawyerPartnerModal";
import { extractActiveLawyerCandidates, filterDisplayPoles } from "../../utils/lawyerPartners";

// Move createSearchRegex outside component to avoid recreation
const createSearchRegex = (searchTerm) => {
  if (!searchTerm || searchTerm.trim().length < 2) return null;

  const normalizedTerm = searchTerm
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

  // Escape special regex characters
  const escaped = normalizedTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Create pattern that matches with or without accents
  const pattern = escaped
    .split("")
    .map((char) => {
      switch (char) {
        case "a":
          return "[aáàâãä]";
        case "e":
          return "[eéèêë]";
        case "i":
          return "[iíìîï]";
        case "o":
          return "[oóòôõö]";
        case "u":
          return "[uúùûü]";
        case "c":
          return "[cç]";
        case "n":
          return "[nñ]";
        default:
          return char;
      }
    })
    .join("");

  return new RegExp(pattern, "gi");
};

// Function to create flexible regex for terms with accent variations
const createFlexibleRegex = (term) => {
  if (!term) return null;

  // Create pattern that matches with or without accents
  const pattern = term
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents from search term
    .split("")
    .map((char) => {
      // For each character, create a pattern that matches with/without accents
      switch (char) {
        case "a":
          return "[aáàâãä]";
        case "e":
          return "[eéèêë]";
        case "i":
          return "[iíìîï]";
        case "o":
          return "[oóòôõö]";
        case "u":
          return "[uúùûü]";
        case "c":
          return "[cç]";
        case "n":
          return "[nñ]";
        default:
          return char.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape regex special chars
      }
    })
    .join("");

  return new RegExp(pattern, "gi");
};

function stripHtmlTags(html) {
  if (!html) return "";

  return (
    html
      // Remove tags HTML
      .replace(/<[^>]*>/g, "")
      // Decodifica entidades HTML comuns
      .replace(/&nbsp;/g, " ")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      // Remove espaços múltiplos
      .replace(/\s+/g, " ")
      // Remove espaços no início e fim
      .trim()
  );
}

// Enhanced component to highlight classifications and search terms in text content
const HighlightedContent = ({
  content,
  classifications,
  publication,
  searchTerm,
  onSearchResultsChange,
  currentSearchIndex,
}) => {
  // Memoize search results to avoid recalculating on every render
  // Remove HTML tags from content before further processing
  // Use a regex to do a basic strip of any HTML tags.
  content = stripHtmlTags(content);
  const searchResults = useMemo(() => {
    if (!searchTerm || searchTerm.trim().length < 2 || !content) return [];

    const searchRegex = createSearchRegex(searchTerm);
    if (!searchRegex) return [];

    const results = [];
    let match;
    while ((match = searchRegex.exec(content)) !== null) {
      results.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        type: "search",
        searchTerm: searchTerm,
      });

      // Prevent infinite loop
      if (match[0].length === 0) {
        searchRegex.lastIndex = match.index + 1;
      }
    }

    return results;
  }, [content, searchTerm]);

  // Notify parent about search results change using useEffect
  useEffect(() => {
    if (onSearchResultsChange) {
      onSearchResultsChange(searchResults);
    }
  }, [searchResults, onSearchResultsChange]);

  // Split content into parts and highlight matches
  const highlightText = useMemo(() => {
    if (!content) return content;
    const matches = [];

    // 1. Find classification matches with position data
    if (classifications && classifications.length > 0) {
      classifications.forEach((classification, index) => {
        const term =
          classification.name ||
          classification.classificacao ||
          classification.description;
        if (!term) return;

        // Check if classification has position data
        if (
          classification.positionStart !== undefined &&
          classification.positionEnd !== undefined
        ) {
          // Use exact position from API
          const positionMatch = {
            start: classification.positionStart,
            end: classification.positionEnd,
            text: content.substring(
              classification.positionStart,
              classification.positionEnd,
            ),
            type: "classification",
            classification: classification,
            term: term,
            contextLine: classification.contextLine,
            fromPosition: true,
          };

          // Validate position is within content bounds
          if (positionMatch.start >= 0 && positionMatch.end <= content.length) {
            matches.push(positionMatch);
          }
        } else if (
          classification.contextLine &&
          classification.contextLine.trim()
        ) {
          // Use contextLine for highlighting when position is not available
          const contextRegex = createFlexibleRegex(
            classification.contextLine.trim(),
          );
          if (contextRegex) {
            let contextMatch;
            while ((contextMatch = contextRegex.exec(content)) !== null) {
              matches.push({
                start: contextMatch.index,
                end: contextMatch.index + contextMatch[0].length,
                text: contextMatch[0],
                type: "classification",
                classification: classification,
                term: term,
                contextLine: classification.contextLine,
                fromContext: true,
              });
              // Prevent infinite loop
              if (contextRegex.lastIndex === contextMatch.index) {
                contextRegex.lastIndex++;
              }
            }
          }
        } else {
          // Fallback to regex search for legacy data
          const regex = createFlexibleRegex(term);
          if (!regex) return;

          let match;
          while ((match = regex.exec(content)) !== null) {
            matches.push({
              start: match.index,
              end: match.index + match[0].length,
              text: match[0],
              type: "classification",
              classification: classification,
              term: term,
              fromPosition: false,
            });

            // Prevent infinite loop if match is zero-length
            if (match[0].length === 0) {
              regex.lastIndex = match.index + 1;
            }
          }
        }
      });
    }

    // 2. Find monetary values
    if (publication?.monetaryValues && publication.monetaryValues.length > 0) {
      publication.monetaryValues.forEach((valorObj) => {
        const valor = valorObj.normalizedText || valorObj.originalText;
        if (!valor) return;

        const escapedValue = valor.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(escapedValue, "gi");
        let match;

        while ((match = regex.exec(content)) !== null) {
          matches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            type: "value",
            value: valor,
          });

          // Prevent infinite loop if match is zero-length
          if (match[0].length === 0) {
            regex.lastIndex = match.index + 1;
          }
        }
      });
    }

    // 3. Find party names
    if (publication?.parties) {
      const allParties = [
        ...(publication.parties.activePoles || []),
        ...(publication.parties.passivePoles || []),
      ];

      allParties.forEach((party) => {
        if (!party?.name) return;

        const escapedName = party.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(escapedName, "gi");
        let match;

        while ((match = regex.exec(content)) !== null) {
          matches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            type: "party",
            party: party.name,
          });

          // Prevent infinite loop if match is zero-length
          if (match[0].length === 0) {
            regex.lastIndex = match.index + 1;
          }
        }
      });
    }

    // Combine all matches and sort by position
    const allMatches = [...searchResults, ...matches];
    allMatches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (prioritize search matches, then by order)
    const nonOverlapping = [];
    allMatches.forEach((match) => {
      const hasOverlap = nonOverlapping.some(
        (existing) =>
          (match.start >= existing.start && match.start < existing.end) ||
          (match.end > existing.start && match.end <= existing.end),
      );
      if (!hasOverlap) {
        nonOverlapping.push(match);
      }
    });

    // If no matches found, return original content
    if (nonOverlapping.length === 0) {
      return content;
    }

    // Build JSX with highlighted parts
    const elements = [];
    let currentIndex = 0;

    nonOverlapping.forEach((match, index) => {
      // Add text before match
      if (currentIndex < match.start) {
        elements.push(
          <span key={`text-${index}`}>
            {content.substring(currentIndex, match.start)}
          </span>,
        );
      }

      // Add highlighted match with different styles based on type
      const getHighlightStyle = (match) => {
        switch (match.type) {
          case "search":
            // Special styling for search matches with current match indicator
            const isCurrentMatch =
              currentSearchIndex >= 0 &&
              searchResults.length > 0 &&
              searchResults.findIndex(
                (sm) => sm.start === match.start && sm.end === match.end,
              ) === currentSearchIndex;
            return isCurrentMatch
              ? "bg-yellow-400 text-yellow-900 border-2 border-yellow-600 shadow-lg"
              : "bg-yellow-200 text-yellow-900 border border-yellow-400";
          case "classification":
            // Enhanced styling for classifications with position data
            const hasPositionData = match.fromPosition === true;
            const isContextMatch = match.fromContext === true;

            let baseClassStyle, opacity;
            if (hasPositionData) {
              baseClassStyle = "border-2 border-dashed"; // Dashed border for position-based highlighting
              opacity = "";
            } else if (isContextMatch) {
              baseClassStyle = "border border-dotted"; // Dotted border for context highlighting
              opacity = "opacity-75"; // More subtle for context
            } else {
              baseClassStyle = "border border-solid"; // Solid border for regex-based highlighting
              opacity = "";
            }

            // Priority-based colors for classifications
            const priority = match.classification?.priority?.toUpperCase();

            switch (priority) {
              case "URGENTE":
                return `bg-red-200 text-red-900 ${baseClassStyle} border-red-400 ${opacity}`;
              case "ALTA":
                return `bg-yellow-200 text-yellow-900 ${baseClassStyle} border-yellow-400 ${opacity}`;
              case "NORMAL":
                return `bg-blue-200 text-blue-900 ${baseClassStyle} border-blue-400 ${opacity}`;
              case "BAIXA":
                return `bg-gray-200 text-gray-900 ${baseClassStyle} border-gray-400 ${opacity}`;
              case "IA":
                return `bg-black text-white ${baseClassStyle} border-black ${opacity}`;
              default:
                return `bg-gray-200 text-gray-900 ${baseClassStyle} border-gray-400 ${opacity}`;
            }
          case "value":
            // Green for monetary values
            return "bg-green-200 text-green-900 border border-green-400";
          case "party":
            // Orange for parties
            return "bg-orange-200 text-orange-900 border border-orange-400";
          default:
            return "bg-gray-200 text-gray-900 border border-gray-400";
        }
      };

      const getTooltipText = (match) => {
        switch (match.type) {
          case "search":
            const matchIndex = searchResults.findIndex(
              (sm) => sm.start === match.start && sm.end === match.end,
            );
            return `🔍 Resultado da busca: "${match.searchTerm}" (${
              matchIndex + 1
            }/${searchResults.length})`;
          case "classification":
            const priority =
              match.classification.priority ||
              match.classification.prioridade ||
              "Normal";
            const classType =
              match.classification.type || match.classification.tipo || "Geral";
            let highlightType;
            if (match.fromPosition) {
              highlightType = " (Posição Exata da API)";
            } else if (match.fromContext) {
              highlightType = " (Linha de Contexto da API)";
            } else {
              highlightType = " (Busca por Termo)";
            }
            const contextInfo = match.contextLine
              ? ` - Contexto: "${match.contextLine.substring(0, 50)}..."`
              : "";
            return `🏷️ Classificação: ${match.term} - Prioridade: ${priority} - Tipo: ${classType}${highlightType}${contextInfo}`;
          case "value":
            return `💰 Valor Monetário: ${match.value}`;
          case "party":
            return `👤 Parte: ${match.party}`;
          default:
            return match.text;
        }
      };

      // Add search result ID for scrolling
      const searchResultId =
        match.type === "search"
          ? `search-result-${searchResults.findIndex(
              (sm) => sm.start === match.start && sm.end === match.end,
            )}`
          : undefined;

      elements.push(
        <span
          key={`highlight-${index}`}
          id={searchResultId}
          className={`inline px-1 py-0.5 rounded text-xs font-medium ${getHighlightStyle(
            match,
          )}`}
          title={getTooltipText(match)}
        >
          {match.text}
        </span>,
      );

      currentIndex = match.end;
    });

    // Add remaining text
    if (currentIndex < content.length) {
      elements.push(
        <span key="text-final">{content.substring(currentIndex)}</span>,
      );
    }

    return elements;
  }, [
    content,
    classifications,
    publication,
    searchResults,
    currentSearchIndex,
  ]);

  return <>{highlightText}</>;
};

const PublicationDjeDetailsSidebar = ({
  publication,
  isOpen,
  onClose,
  onPartnerCreated,
  onPublicationConverted,
  onPublicationReclassified,
  onPublicationArchived,
  onPublicationUnarchived,
  onPublicationDiscarded,
  onPublicationRestored,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const [viewMode, setViewMode] = useState("overview"); // 'overview' | 'content'
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [isDiscarded, setIsDiscarded] = useState(false);
  const [scoringResult, setScoringResult] = useState(null);
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringError] = useState(null);
  const [contentSearchTerm, setContentSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const contentRef = useRef(null);

  // Estados para o mapa de navegação
  const [contentSections, setContentSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showNavigationSidebar, setShowNavigationSidebar] = useState(true);

  // Estado para controlar a navegação por classificações
  const [currentClassificationState, setCurrentClassificationState] = useState(
    {},
  );
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [showLawyerPartnerModal, setShowLawyerPartnerModal] = useState(false);

  // Estados para resumo de IA
  const [aiSummary, setAiSummary] = useState(null);
  const [isGeneratingAiSummary, setIsGeneratingAiSummary] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState(null);

  // Estado para controlar expansão das classificações
  const [classificationsExpanded, setClassificationsExpanded] = useState(false);

  const handleConvertToProcess = async () => {
    if (!publication?.id || isConverting) return;

    setIsConverting(true);
    try {
      const processData = {
        publicationId: publication.id,
        numeroProcesso: publication.numeroProcesso,
        tribunal: publication.tribunal,
        vara: publication.vara,
        esfera: publication.detalhes?.esfera,
        estado: publication.detalhes?.estado,
        classificacoes: publication.classifications?.map((c) => c.name) || [],
        valorEstimado: publication.detalhes?.valores?.[0]?.valor || null,
        dataPublicacao: publication.dataPublicacao,
        resumo: publication.summary,
        conteudo:
          publication.detalhes?.inteiroTeor || publication.detalhes?.conteudo,
        score: publication.score || 0,
        status: "AGUARDANDO_ANALISE",
      };

      const newProcess = await apiService.post("/processes", processData);

      onPublicationConverted?.(publication.id, {
        processId: newProcess.id,
        processStatus: newProcess.status,
      });

      alert("Processo criado com sucesso! ID: " + newProcess.id);
      onClose();
    } catch (error) {
      console.error("Erro ao converter publicação em processo:", error);
      const apiMessage = error?.response?.data?.error;
      alert(
        apiMessage ||
          "Erro ao converter publicação em processo. Tente novamente.",
      );
    } finally {
      setIsConverting(false);
      setShowConversionModal(false);
    }
  };

  const handleArchive = async () => {
    if (!publication?.id || isArchiving) return;

    setIsArchiving(true);
    try {
      const result = await apiService.put(
        `/publications-dje/${publication.id}/archive`,
        {
          razao: "Geral",
        },
      );

      if (result?.success) {
        setIsArchived(true);
        if (publication) {
          publication.arquivada = true;
        }
        onPublicationArchived?.(
          publication.id,
          publication.numeroProcesso ||
            publication.detalhes?.numeroProcesso,
        );
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao arquivar publicação. Tente novamente.");
    } finally {
      setIsArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    if (!publication?.id || isUnarchiving || isDiscarded) return;

    const confirmed = window.confirm(
      "Desarquivar esta publicação? Ela voltará à listagem de novas.",
    );
    if (!confirmed) return;

    setIsUnarchiving(true);
    try {
      const result = await publicationsDjeService.unarchivePublication(
        publication.id,
      );

      if (result?.success) {
        setIsArchived(false);
        if (publication) {
          publication.arquivada = false;
        }
        onPublicationUnarchived?.(
          publication.id,
          publication.numeroProcesso ||
            publication.detalhes?.numeroProcesso,
        );
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao desarquivar publicação. Tente novamente.");
    } finally {
      setIsUnarchiving(false);
    }
  };

  const handleDiscard = async () => {
    if (!publication?.id || isDiscarding) return;

    const confirmed = window.confirm(
      "Descartar esta publicação?\n\nO processo será arquivado e NENHUMA nova atualização deste caso será sincronizada novamente.",
    );
    if (!confirmed) return;

    setIsDiscarding(true);
    try {
      const result = await publicationsDjeService.discardPublication(
        publication.id,
      );

      if (result?.success) {
        setIsArchived(true);
        setIsDiscarded(true);
        if (publication) {
          publication.arquivada = true;
          publication.descartada = true;
        }
        onPublicationDiscarded?.(
          publication.id,
          publication.numeroProcesso ||
            publication.detalhes?.numeroProcesso,
        );
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao descartar publicação. Tente novamente.");
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleRestore = async () => {
    if (!publication?.id || isRestoring) return;

    const confirmed = window.confirm(
      "Reabilitar esta publicação?\n\nO processo voltará a poder ser sincronizado e as publicações serão desarquivadas.",
    );
    if (!confirmed) return;

    setIsRestoring(true);
    try {
      const result = await publicationsDjeService.restorePublication(
        publication.id,
      );

      if (result?.success) {
        setIsArchived(false);
        setIsDiscarded(false);
        if (publication) {
          publication.arquivada = false;
          publication.descartada = false;
        }
        onPublicationRestored?.(
          publication.id,
          publication.numeroProcesso ||
            publication.detalhes?.numeroProcesso,
        );
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      alert(apiMessage || "Erro ao reabilitar publicação. Tente novamente.");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleReclassify = async () => {
    if (!publication?.id || isReclassifying || !isAdmin) return;

    const confirmed = window.confirm(
      "Reclassificar esta publicação com GPT e regras atuais? Isso pode alterar classificações, score e status de arquivamento.",
    );
    if (!confirmed) return;

    setIsReclassifying(true);
    try {
      const response = await publicationsDjeService.reclassifyPublication(
        publication.id,
      );
      const updatedPublication = response?.publication;

      if (updatedPublication) {
        onPublicationReclassified?.(updatedPublication);
        setIsArchived(Boolean(updatedPublication.arquivada));
      }

      const archivedMessage = response?.result?.archived
        ? "Publicação reclassificada e arquivada automaticamente."
        : "Publicação reclassificada com sucesso.";
      alert(archivedMessage);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao reclassificar publicação.";
      alert(errorMessage);
    } finally {
      setIsReclassifying(false);
    }
  };

  const handleGenerateAiSummary = async () => {
    if (!publication?.id || isGeneratingAiSummary) return;

    setIsGeneratingAiSummary(true);
    setAiSummaryError(null);

    try {
      const response = await apiService.post(
        `/publications-dje/${publication.id}/generate-ai-summary`,
        {},
      );

      const generatedSummary =
        typeof response?.summary === "string" ? response.summary.trim() : null;
      const generatedAt = response?.generatedAt || new Date().toISOString();

      if (generatedSummary) {
        setAiSummary(generatedSummary);

        publication.resumoIa = generatedSummary;
        publication.resumoIaGeneratedAt = generatedAt;
        if (publication.detalhes) {
          publication.detalhes.resumoIa = generatedSummary;
          publication.detalhes.resumoIaGeneratedAt = generatedAt;
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível gerar o resumo de IA.";

      setAiSummaryError(errorMessage);
    } finally {
      setIsGeneratingAiSummary(false);
    }
  };

  // Extrair scoring que já vem integrado da API de detalhes da publicação
  const extractScoring = () => {
    if (!publication) return;

    // Usar campos diretos de scoring da publicação (dados do banco)
    if (publication.score !== null && publication.score !== undefined) {
      // Determinar classificação baseado no score
      let classification = "REMOTO";
      if (publication.score >= 76) {
        classification = "EXCEPCIONAL";
      } else if (publication.score >= 66) {
        classification = "PROVÁVEL";
      } else if (publication.score >= 41) {
        classification = "POSSÍVEL";
      } else if (publication.score >= 11) {
        classification = "REMOTO";
      } else {
        classification = "INAPTO";
      }

      // Extrair fatores de influência do scoreBreakdown
      let fatoresInfluencia = [];

      if (publication.scoreBreakdown) {
        try {
          const factors =
            typeof publication.scoreBreakdown === "string"
              ? JSON.parse(publication.scoreBreakdown)
              : publication.scoreBreakdown;

          // Usar a estrutura real dos scoreBreakdown
          if (factors.factors && Array.isArray(factors.factors)) {
            fatoresInfluencia = [...factors.factors];
          }
        } catch (error) {
          fatoresInfluencia = ["Erro ao processar fatores do scoring"];
        }
      }

      // Se não conseguiu extrair fatores, usar mensagem padrão
      if (fatoresInfluencia.length === 0) {
        fatoresInfluencia = ["Fatores de scoring não disponíveis"];
      }

      setScoringResult({
        score: publication.score,
        classificacao: classification,
        fatoresInfluencia: fatoresInfluencia,
        recomendacao: `Processo classificado como ${classification}`,
      });
      setScoringLoading(false);
    } else {
      // Fallback caso não tenha scoring
      setScoringResult({
        score: 0,
        classificacao: "PENDENTE",
        fatoresInfluencia: ["Scoring não disponível"],
        recomendacao: "Aguardar processamento do scoring",
      });
      setScoringLoading(false);
    }
  };

  const currentScoring = scoringResult || {
    score: 0,
    classificacao: "REMOTO",
    fatoresInfluencia: [],
    recomendacao: "Processando...",
  };

  const getClassificationColor = (classificacao) => {
    switch (classificacao) {
      case "EXCEPCIONAL":
        return "text-purple-600 bg-purple-100 border-purple-200";
      case "PROVÁVEL":
        return "text-green-600 bg-green-100 border-green-200";
      case "POSSÍVEL":
        return "text-blue-600 bg-blue-100 border-blue-200";
      case "REMOTO":
        return "text-orange-600 bg-orange-100 border-orange-200";
      case "INAPTO":
        return "text-red-600 bg-red-100 border-red-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 76) return "bg-purple-600 hover:bg-purple-700"; // EXCEPCIONAL
    if (score >= 66) return "bg-green-600 hover:bg-green-700"; // PROVÁVEL
    if (score >= 41) return "bg-blue-600 hover:bg-blue-700"; // POSSÍVEL
    if (score >= 11) return "bg-orange-600 hover:bg-orange-700"; // REMOTO
    return "bg-red-600 hover:bg-red-700"; // INAPTO
  };

  const getClassificationIcon = (classificacao) => {
    switch (classificacao) {
      case "EXCEPCIONAL":
        return <TrendingUp className="w-4 h-4" />;
      case "PROVÁVEL":
        return <TrendingUp className="w-4 h-4" />;
      case "POSSÍVEL":
        return <Eye className="w-4 h-4" />;
      case "REMOTO":
        return <Clock className="w-4 h-4" />;
      case "INAPTO":
        return <X className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Handle search results change
  const handleSearchResultsChange = (results) => {
    setSearchResults(results);
    if (results.length > 0 && currentSearchIndex === -1) {
      setCurrentSearchIndex(0);
    } else if (results.length === 0) {
      setCurrentSearchIndex(-1);
    }
  };

  // Navigate search results
  const navigateToSearchResult = (direction) => {
    if (searchResults.length === 0) return;

    let newIndex;
    if (direction === "next") {
      newIndex =
        currentSearchIndex >= searchResults.length - 1
          ? 0
          : currentSearchIndex + 1;
    } else {
      newIndex =
        currentSearchIndex <= 0
          ? searchResults.length - 1
          : currentSearchIndex - 1;
    }

    setCurrentSearchIndex(newIndex);

    // Scroll to the result
    setTimeout(() => {
      const element = document.getElementById(`search-result-${newIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Função para detectar seções no texto
  const detectContentSections = (text) => {
    if (!text) return [];

    const sections = [];
    const lines = text.split("\n");

    // Padrões mais abrangentes para textos jurídicos
    const sectionPatterns = [
      {
        pattern: /^\s*(EMENTA|SÚMULA):?/i,
        name: "Ementa",
        icon: "📋",
        priority: 1,
      },
      {
        pattern: /^\s*(VISTOS|RELATÓRIO):?/i,
        name: "Relatório",
        icon: "📄",
        priority: 2,
      },
      {
        pattern: /^\s*(FUNDAMENTAÇÃO|FUNDAMENTO|MÉRITO):?/i,
        name: "Fundamentação",
        icon: "📝",
        priority: 3,
      },
      {
        pattern: /^\s*(DECISÃO|DECIDE|JULGO|SENTENÇA|DESPACHO):?/i,
        name: "Decisão",
        icon: "⚖️",
        priority: 4,
      },
      {
        pattern: /^\s*(DISPOSITIVO|DISPOSITIVA|ANTE O EXPOSTO):?/i,
        name: "Dispositivo",
        icon: "📜",
        priority: 5,
      },
      {
        pattern: /^\s*(RECURSO|APELAÇÃO|AGRAVO|EMBARGOS):?/i,
        name: "Recurso",
        icon: "🔄",
        priority: 6,
      },
      {
        pattern: /^\s*(CUSTAS|HONORÁRIOS|VALOR|CONDENAÇÃO|MULTA):?/i,
        name: "Valores",
        icon: "💰",
        priority: 7,
      },
      {
        pattern: /^\s*(CONSIDERAÇÕES|CONSIDERANDO|VOTO):?/i,
        name: "Considerações",
        icon: "🤔",
        priority: 8,
      },
      {
        pattern: /^\s*(CONCLUSÃO|ASSIM):?/i,
        name: "Conclusão",
        icon: "✅",
        priority: 9,
      },
      {
        pattern: /^\s*(PARTES|REQUERENTE|REQUERIDO|AUTOR|RÉU):?/i,
        name: "Partes",
        icon: "👥",
        priority: 10,
      },
      {
        pattern: /^\s*(ADVOGADO|PROCURADOR|DEFENSOR):?/i,
        name: "Representação",
        icon: "👨‍⚖️",
        priority: 11,
      },
      {
        pattern: /^\s*(PROCESSO|AUTOS):?\s*N[ºª]?:?\s*[\d\-\.]+/i,
        name: "Processo",
        icon: "🆔",
        priority: 0,
      },
      {
        pattern: /^\s*(TRIBUNAL|JUÍZO|VARA|COMARCA):?/i,
        name: "Competência",
        icon: "🏛️",
        priority: 1,
      },
      {
        pattern: /^\s*(PUBLICAÇÃO|INTIMAÇÃO|CITAÇÃO):?/i,
        name: "Comunicação",
        icon: "📢",
        priority: 12,
      },
    ];

    let currentPosition = 0;

    lines.forEach((line, index) => {
      sectionPatterns.forEach(({ pattern, name, icon }) => {
        if (pattern.test(line) && line.trim().length > 3) {
          const lineStart = text.indexOf(line, currentPosition);
          if (lineStart !== -1) {
            sections.push({
              id: `section-${sections.length}`,
              name,
              icon,
              lineNumber: index + 1,
              position: lineStart,
              text: line.trim(),
            });
            currentPosition = lineStart + line.length;
          }
        }
      });
    });

    // Adicionar seções automáticas baseadas no tamanho do texto
    if (text.length > 5000 && sections.length <= 2) {
      const quarterLength = Math.floor(text.length / 4);
      const halfLength = Math.floor(text.length / 2);
      const threeQuarterLength = Math.floor((text.length * 3) / 4);

      const autoSections = [
        {
          name: "Início",
          icon: "🏁",
          lineNumber: 1,
          position: 0,
          text: "Início do documento",
        },
        {
          name: "1º Quarto",
          icon: "📄",
          lineNumber: Math.floor(quarterLength / 50),
          position: quarterLength,
          text: "Primeiro quarto do texto",
        },
        {
          name: "Meio",
          icon: "📖",
          lineNumber: Math.floor(halfLength / 50),
          position: halfLength,
          text: "Meio do documento",
        },
        {
          name: "3º Quarto",
          icon: "📄",
          lineNumber: Math.floor(threeQuarterLength / 50),
          position: threeQuarterLength,
          text: "Terceiro quarto do texto",
        },
        {
          name: "Final",
          icon: "🏁",
          lineNumber: lines.length,
          position: text.length - 100,
          text: "Final do documento",
        },
      ];

      autoSections.forEach((section, index) => {
        sections.push({
          ...section,
          id: `auto-section-${index}`,
        });
      });
    }

    // Se não encontrar seções, criar seções padrão
    if (sections.length === 0) {
      sections.push({
        id: "default-section-0",
        name: "Conteúdo Completo",
        icon: "📋",
        lineNumber: 1,
        position: 0,
        text: "Início do documento",
      });
    }

    // Ordenar seções por posição, depois por prioridade para casos de mesma posição
    sections.sort((a, b) => {
      if (a.position === b.position) {
        return (a.priority || 999) - (b.priority || 999);
      }
      return a.position - b.position;
    });

    return sections;
  };

  // Função para navegar para uma seção (por índice)
  const navigateToSection = (sectionIndex) => {
    const section = contentSections[sectionIndex];
    if (!section || !contentRef.current) return;

    setCurrentSection(sectionIndex);

    // Encontrar o elemento com o texto da seção e fazer scroll
    const contentElement = contentRef.current;
    const contentText = publication.conteudo || publication.texto || "";

    // Calcular a posição aproximada da seção no conteúdo
    const sectionPosition = section.position;
    const totalLength = contentText.length;
    const scrollPercentage =
      totalLength > 0 ? (sectionPosition / totalLength) * 100 : 0;

    // Calcular a posição de scroll baseada na porcentagem
    const scrollHeight =
      contentElement.scrollHeight - contentElement.clientHeight;
    const targetScrollTop = (scrollPercentage / 100) * scrollHeight;

    // Fazer scroll suave para a posição calculada
    contentElement.scrollTo({
      top: Math.max(0, targetScrollTop - 100), // Offset para melhor visualização
      behavior: "smooth",
    });
  };

  // Função para calcular progresso do scroll
  const updateScrollProgress = () => {
    if (!contentRef.current) return;

    const element = contentRef.current;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight - element.clientHeight;
    const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

    setScrollProgress(Math.min(100, Math.max(0, progress)));

    // Determinar seção atual baseada no progresso de scroll
    if (contentSections.length > 0) {
      let newCurrentSection = 0;
      const contentText = publication.conteudo || publication.texto || "";

      if (contentText) {
        // Calcular a posição atual no texto baseada no scroll
        const currentTextPosition = (progress / 100) * contentText.length;

        // Encontrar a seção mais próxima da posição atual
        for (let i = contentSections.length - 1; i >= 0; i--) {
          const section = contentSections[i];

          // Se a posição atual do texto está após o início desta seção
          if (currentTextPosition >= section.position) {
            newCurrentSection = i;
            break;
          }
        }
      }

      if (newCurrentSection !== currentSection) {
        setCurrentSection(newCurrentSection);
      }
    }
  };

  // Função para copiar conteúdo da publicação
  const copyPublicationContent = async () => {
    try {
      const content = publication.detalhes.conteudo || publication.texto || "";
      if (!content) {
        alert("❌ Conteúdo não disponível para copiar");
        return;
      }

      // Preparar dados da publicação
      const publicationData = {
        numeroProcesso: publication.numeroProcesso,
        dataPublicacao: publication.dataDisponibilizacao,
        tribunal: publication.tribunal,
        tipoDocumento: publication.tipoDocumento,
        classificacoes:
          publication.classifications?.map((c) => c.name).join(", ") || "N/A",
        conteudo: content,
      };

      // Formatar para texto estruturado
      const formattedContent = `
📋 PUBLICAÇÃO DJE - ECP CAPITAL

🆔 Processo: ${publicationData.numeroProcesso}
📅 Data: ${publicationData.dataPublicacao}
🏛️ Tribunal: ${publicationData.tribunal}
📄 Tipo: ${publicationData.tipoDocumento}
🏷️ Classificações: ${publicationData.classificacoes}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📄 CONTEÚDO INTEGRAL:

${publicationData.conteudo}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🏢 Copiado via ECP Capital (${new Date().toLocaleDateString(
        "pt-BR",
      )} ${new Date().toLocaleTimeString("pt-BR")})
`.trim();

      // Tentar usar a API moderna de clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(formattedContent);
        alert("✅ Conteúdo copiado com sucesso!");
      } else {
        // Fallback para navegadores antigos
        const textArea = document.createElement("textarea");
        textArea.value = formattedContent;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand("copy");
          alert("✅ Conteúdo copiado com sucesso!");
        } catch (err) {
          alert("❌ Erro ao copiar conteúdo. Tente novamente.");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      alert(
        "❌ Erro ao copiar conteúdo. Verifique as permissões do navegador.",
      );
    }
  };

  // Reset search when search term changes
  useEffect(() => {
    if (contentSearchTerm.trim().length < 2) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
    }
  }, [contentSearchTerm]);

  // Scroll to first result when search results change
  useEffect(() => {
    if (searchResults.length > 0 && currentSearchIndex === 0) {
      setTimeout(() => {
        const element = document.getElementById("search-result-0");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 200);
    }
  }, [searchResults]);

  // Reset to overview tab whenever sidebar opens and extract scoring
  useEffect(() => {
    if (isOpen && publication) {
      setViewMode("overview");
      setContentSearchTerm(""); // Reset search term
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      extractScoring();

      // Initialize archived state based on publication data
      const initialArchivedState =
        publication?.arquivada || publication?.detalhes?.arquivada || false;
      setIsArchived(initialArchivedState);
      const initialDiscardedState =
        publication?.descartada ||
        publication?.detalhes?.descartada ||
        false;
      setIsDiscarded(initialDiscardedState);
      setAiSummaryError(null);

      // Carregar resumo de IA existente se disponível
      const existingSummary = publication.resumoIa;
      if (existingSummary) {
        setAiSummary(existingSummary);
      } else {
        setAiSummary(null);
      }
    }
  }, [isOpen, publication]);

  // Effect para detectar seções do conteúdo
  useEffect(() => {
    if (publication) {
      const content = publication.conteudo || publication.texto || "";
      const sections = detectContentSections(content);
      setContentSections(sections);
      setCurrentSection(0);
      setScrollProgress(0);

      // Reset classification navigation state when changing publication
      setCurrentClassificationState({});
    }
  }, [publication]);

  // Effect para adicionar listener de scroll
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    contentElement.addEventListener("scroll", updateScrollProgress);
    return () =>
      contentElement.removeEventListener("scroll", updateScrollProgress);
  }, [contentSections]);

  // Effect para scroll para o topo sempre que entrar no modo conteúdo
  useEffect(() => {
    if (viewMode === "content" && contentRef.current) {
      // Scroll para o topo imediatamente ao entrar no modo conteúdo
      contentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      // Reset da seção atual e progresso
      setCurrentSection(0);
      setScrollProgress(0);
    }
  }, [viewMode]);

  if (!isOpen || !publication) return null;

  // Extrair dados da publicação
  const classifications = publication.classifications || [];
  const parties = publication.parties || { activePoles: [], passivePoles: [] };
  const displayParties = filterDisplayPoles(parties);
  const activeLawyerCandidates = extractActiveLawyerCandidates(parties);
  const monetaryValues = publication.monetaryValues || [];
  const isArchivedPublication =
    isArchived || publication?.arquivada || publication?.detalhes?.arquivada;
  const shouldShowGenerateSummaryButton = isArchivedPublication || !aiSummary;

  return (
    <div>
      {/* Backdrop with dynamic opacity */}
      <div
        className={`fixed top-0 left-0 w-[100vw] h-[100vh] bg-black z-[9998] transition-all duration-500 ${
          viewMode === "content"
            ? "bg-opacity-75 backdrop-blur-sm"
            : "bg-opacity-50"
        }`}
        onClick={onClose}
      />

      {/* Sidebar Panel - Fullscreen */}
      <div
        className={`fixed bg-white shadow-2xl z-[9999] flex flex-col transition-all duration-500 ease-out top-0 right-0 h-screen w-full`}
        style={{
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Fixed Header with Tab Navigation - Enhanced with expansion effect */}
        <div
          className={`flex-shrink-0 border-b border-gray-200 bg-white transition-all duration-500 ${
            viewMode === "content"
              ? "shadow-2xl bg-gradient-to-r from-white via-blue-50 to-indigo-50 border-blue-200"
              : "shadow-lg"
          }`}
        >
          <div className="p-4 sm:p-6 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Hash className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Detalhes da Publicação
                </h2>
              </div>

              {/* Status badges with close button */}
              <div className="flex items-center gap-2">
                {/* Novo sistema de scoring ECP Capital */}
                <div className="relative group">
                  <span
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold shadow-sm cursor-help border ${getClassificationColor(
                      currentScoring.classificacao,
                    )} ${scoringLoading ? "animate-pulse" : ""}`}
                  >
                    {scoringLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Analisando...
                      </>
                    ) : scoringError ? (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        Erro no Scoring
                      </>
                    ) : (
                      <>
                        {getClassificationIcon(currentScoring.classificacao)}
                        {currentScoring.score}/100{" "}
                        {currentScoring.classificacao}
                      </>
                    )}
                  </span>

                  {/* Tooltip com nova metodologia */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 max-w-sm">
                    <div className="text-center font-semibold mb-2">
                      🏛️ Análise ECP Capital
                    </div>
                    {scoringError ? (
                      <div className="text-red-300">Erro: {scoringError}</div>
                    ) : (
                      <>
                        <div className="space-y-1 mb-2">
                          <div className="flex justify-between">
                            <span>Score:</span>
                            <span className="font-bold">
                              {currentScoring.score}/100
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Classificação:</span>
                            <span className="font-bold">
                              {currentScoring.classificacao}
                            </span>
                          </div>
                        </div>

                        {currentScoring.fatoresInfluencia?.length > 0 && (
                          <>
                            <div className="text-center font-semibold mb-1 pt-2 border-t border-gray-700">
                              Fatores de Influência:
                            </div>
                            <div className="space-y-0.5 text-left">
                              {currentScoring.fatoresInfluencia.map(
                                (fator, index) => (
                                  <div key={index} className="text-xs">
                                    • {fator}
                                  </div>
                                ),
                              )}
                            </div>
                          </>
                        )}

                        <div className="text-center font-semibold mt-2 pt-2 border-t border-gray-700 text-yellow-300">
                          💡 {currentScoring.recomendacao}
                        </div>
                      </>
                    )}

                    {/* Arrow */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900"></div>
                  </div>
                </div>

                {/* Read Status Badge - Visual Only */}
                <div
                  className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold ${
                    publication.isRead || false
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                  }`}
                >
                  {publication.lida || false ? (
                    <>
                      <CheckCircle className="w-3 h-3 mr-1" /> Lida
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3 mr-1" /> Não Lida
                    </>
                  )}
                </div>

                {/* Conversão em processo */}
                {publication.convertedToProcess && publication.processId ? (
                  <ConvertedToProcessBadge
                    processId={publication.processId}
                    processStatus={publication.processStatus}
                  />
                ) : (
                  <button
                    onClick={() => setShowConversionModal(true)}
                    disabled={isConverting}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all touch-manipulation ${
                      isConverting
                        ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                        : "bg-green-100 text-green-800 border border-green-200 hover:bg-green-200 hover:border-green-300"
                    }`}
                    title="Converter publicação em processo (conversão manual)"
                  >
                    <ArrowRight className="w-3 h-3" />
                    {isConverting ? "Convertendo..." : "Processo"}
                  </button>
                )}

                {isAdmin && (
                  <button
                    onClick={handleReclassify}
                    disabled={isReclassifying}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all touch-manipulation ${
                      isReclassifying
                        ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                        : "bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-200 hover:border-indigo-300"
                    }`}
                    title="Reclassificar publicação com GPT e regras atuais"
                  >
                    <RefreshCw
                      className={`w-3 h-3 ${isReclassifying ? "animate-spin" : ""}`}
                    />
                    {isReclassifying ? "Reclassificando..." : "Reclassificar"}
                  </button>
                )}

                {/* Archive status and button */}
                {isArchived ? (
                  <>
                    <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200">
                      <Archive className="w-3 h-3 mr-1" />
                      Arquivada
                    </div>
                    {!isDiscarded && (
                      <button
                        onClick={handleUnarchive}
                        disabled={
                          isArchiving ||
                          isUnarchiving ||
                          isDiscarding ||
                          isRestoring
                        }
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all touch-manipulation ${
                          isArchiving ||
                          isUnarchiving ||
                          isDiscarding ||
                          isRestoring
                            ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                            : "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200 hover:border-emerald-300"
                        }`}
                        title="Desarquivar publicação"
                      >
                        <RotateCcw
                          className={`w-3 h-3 ${isUnarchiving ? "animate-spin" : ""}`}
                        />
                        {isUnarchiving ? "Desarquivando..." : "Desarquivar"}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleArchive}
                    disabled={
                      isArchiving || isUnarchiving || isDiscarding || isRestoring
                    }
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all touch-manipulation ${
                      isArchiving ||
                      isUnarchiving ||
                      isDiscarding ||
                      isRestoring
                        ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                        : "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 hover:border-amber-300"
                    }`}
                  >
                    <Archive className="w-3 h-3" />
                    {isArchiving ? "Arquivando..." : "Arquivar"}
                  </button>
                )}

                {/* Discard / Restore */}
                {isDiscarded ? (
                  <>
                    <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                      <Ban className="w-3 h-3 mr-1" />
                      Descartada
                    </div>
                    <button
                      onClick={handleRestore}
                      disabled={
                        isArchiving ||
                        isUnarchiving ||
                        isDiscarding ||
                        isRestoring
                      }
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all touch-manipulation ${
                        isArchiving ||
                        isUnarchiving ||
                        isDiscarding ||
                        isRestoring
                          ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                          : "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200 hover:border-emerald-300"
                      }`}
                      title="Reabilitar: remove do descarte e permite sincronização novamente"
                    >
                      <RotateCcw
                        className={`w-3 h-3 ${isRestoring ? "animate-spin" : ""}`}
                      />
                      {isRestoring ? "Reabilitando..." : "Reabilitar"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleDiscard}
                    disabled={
                      isArchiving || isUnarchiving || isDiscarding || isRestoring
                    }
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all touch-manipulation ${
                      isArchiving ||
                      isUnarchiving ||
                      isDiscarding ||
                      isRestoring
                        ? "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                        : "bg-red-100 text-red-800 border border-red-200 hover:bg-red-200 hover:border-red-300"
                    }`}
                    title="Descartar: arquiva e impede novas sincronizações deste processo"
                  >
                    <Ban className="w-3 h-3" />
                    {isDiscarding ? "Descartando..." : "Descartar"}
                  </button>
                )}

                {/* Close button after status badges */}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation flex-shrink-0"
                  aria-label="Fechar detalhes"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Número do Processo
                  </p>
                  <ProcessNumber
                    processNumber={
                      publication.numeroProcessoFormatted ||
                      publication.numeroProcesso
                    }
                    className="text-lg font-bold text-gray-900 break-all"
                    copyable={true}
                  />

                </div>

                {/* Technical data on same line */}
                <div className="flex gap-4 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-600 mb-1">
                      Data Publicação
                    </p>
                    <p className="text-sm font-mono text-gray-800">
                      {publication.dataDisponibilizacao || "N/A"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-600 mb-1">
                      Tipo Documento
                    </p>
                    <p className="text-sm font-mono text-gray-800">
                      {publication.tipoDocumento || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Classifications rows - Organized by location and process info */}
              <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                {/* Location row: Tribunal, Órgão */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-600">
                      Tribunal:
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 truncate">
                      {publication.tribunal || "N/A"}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-600">
                      Órgão:
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 truncate">
                      {publication.nomeOrgao || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Process Class row */}
                {(publication.nomeClasse || publication.codigoClasse) && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-600">
                      Classe:
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-green-700 truncate">
                      {publication.nomeClasse || "N/A"}
                      {publication.codigoClasse &&
                        ` (${publication.codigoClasse})`}
                    </span>
                  </div>
                )}

                {/* Classifications row */}
                {classifications && classifications.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2">
                    {classifications.map((classification, index) => {
                      const getPriorityColor = (priority) => {
                        switch (priority?.toUpperCase()) {
                          case "URGENTE":
                            return "bg-red-100 text-red-800 border-red-200";
                          case "ALTA":
                            return "bg-yellow-100 text-yellow-800 border-yellow-200";
                          case "NORMAL":
                            return "bg-blue-100 text-blue-800 border-blue-200";
                          case "BAIXA":
                            return "bg-gray-100 text-gray-800 border-gray-200";
                          case "IA":
                            return "bg-black text-white border-black";
                          default:
                            return "bg-gray-100 text-gray-800 border-gray-200";
                        }
                      };

                      return (
                        <span
                          key={classification._id || index}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium border ${getPriorityColor(
                            classification.priority,
                          )}`}
                          title={`Prioridade: ${classification.priority}`}
                          style={{ fontSize: "10px" }}
                        >
                          {classification.name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation - Enhanced with content expansion indicators */}
          <div
            className={`flex border-b border-gray-100 bg-gray-50 transition-all duration-300 ${
              viewMode === "content"
                ? "bg-gradient-to-r from-blue-50 to-indigo-50"
                : ""
            }`}
          >
            <button
              onClick={() => setViewMode("overview")}
              className={`flex-1 px-2 sm:px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-300 touch-manipulation ${
                viewMode === "overview"
                  ? "border-blue-500 text-blue-600 bg-white shadow-sm"
                  : "border-transparent text-gray-600 hover:text-gray-800 hover:bg-white"
              }`}
            >
              📝 Resumo
            </button>
            <button
              onClick={() => setViewMode("content")}
              className={`flex-1 px-2 sm:px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-300 touch-manipulation relative ${
                viewMode === "content"
                  ? "border-blue-500 text-blue-600 bg-white shadow-lg"
                  : "border-transparent text-gray-600 hover:text-gray-800 hover:bg-white"
              }`}
            >
              📄 Conteúdo
              {viewMode === "content" && (
                <>
                  <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full animate-pulse" />
                  <div className="ml-2 inline-flex items-center">
                    <span className="text-xs bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-2 py-0.5 rounded-full animate-pulse font-bold">
                      🖥️ MODO ZEN
                    </span>
                  </div>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scrollable Content - Enhanced with expansion effects */}
        <div
          className={`flex-1 overflow-y-auto transition-all duration-500 ${
            viewMode === "content"
              ? "bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50"
              : "bg-gray-50"
          }`}
        >
          {viewMode === "overview" ? (
            <div className="p-4 sm:p-6 space-y-6">
              {/* AI Summary Section */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between p-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-100 rounded-lg">
                      <Award className="w-4 h-4 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      Resumo de IA
                    </h3>
                  </div>

                  {shouldShowGenerateSummaryButton && (
                    <button
                      type="button"
                      onClick={handleGenerateAiSummary}
                      disabled={isGeneratingAiSummary}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      <FileCheck className="w-3.5 h-3.5" />
                      {isGeneratingAiSummary
                        ? "Gerando..."
                        : aiSummary
                          ? "Gerar novamente"
                          : "Gerar resumo"}
                    </button>
                  )}
                </div>

                <div className="p-3">
                  {aiSummaryError && (
                    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {aiSummaryError}
                    </div>
                  )}

                  {aiSummary ? (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {aiSummary}
                      </div>
                      {publication.resumoIaGeneratedAt && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <span className="text-xs text-gray-500">
                            Gerado em:{" "}
                            {new Date(
                              publication.resumoIaGeneratedAt,
                            ).toLocaleString("pt-BR")}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-sm text-gray-500 text-center py-4">
                        {isArchivedPublication
                          ? "Publicação arquivada não recebe resumo automático. Gere manualmente caso precise consultar esse conteúdo."
                          : "Nenhum resumo de IA disponível ainda. Ele é gerado automaticamente durante a sincronização, mas você também pode gerar manualmente agora."}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Parties Section - MOVIDO PARA CIMA */}
              {(displayParties.activePoles?.length > 0 ||
                displayParties.passivePoles?.length > 0) && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 p-3 border-b border-gray-100">
                    <div className="p-1.5 bg-green-100 rounded-lg">
                      <User className="w-4 h-4 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      Partes Processuais
                    </h3>
                  </div>
                  <div className="p-3 space-y-3">
                    {displayParties.activePoles?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                          <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          Polo Ativo ({displayParties.activePoles.length})
                        </h4>
                        <div className="space-y-1">
                          {displayParties.activePoles.map((pole, index) => (
                            <div
                              key={index}
                              className="border border-gray-200 rounded p-2 bg-gray-50"
                            >
                              <p className="text-xs font-medium text-gray-900">
                                {pole.name}
                              </p>
                              {pole.type && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {pole.type === "PESSOA_FISICA"
                                    ? "Pessoa Física"
                                    : pole.type === "ADVOGADO"
                                      ? "Advogado"
                                      : "Pessoa Jurídica"}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {displayParties.passivePoles?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                          <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                          Polo Passivo ({displayParties.passivePoles.length})
                        </h4>
                        <div className="space-y-1">
                          {displayParties.passivePoles.map((pole, index) => (
                            <div
                              key={index}
                              className="border border-gray-200 rounded p-2 bg-gray-50"
                            >
                              <p className="text-xs font-medium text-gray-900">
                                {pole.name}
                              </p>
                              {pole.type && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {pole.type === "PESSOA_FISICA"
                                    ? "Pessoa Física"
                                    : pole.type === "ADVOGADO"
                                      ? "Advogado"
                                      : "Pessoa Jurídica"}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeLawyerCandidates.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 p-3 border-b border-gray-100">
                    <div className="p-1.5 bg-blue-100 rounded-lg">
                      <Scale className="w-4 h-4 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Advogado</h3>
                  </div>
                  <div className="p-3 space-y-3">
                    {activeLawyerCandidates.map((lawyer, index) => {
                      const matchedPartner = publication.matchedPartners?.find((partner) => {
                        const sameName = (partner?.nome || '').trim().toUpperCase() === (lawyer?.nome || '').trim().toUpperCase();
                        const sameOab = lawyer?.oab && partner?.oab
                          ? partner.oab.trim().toUpperCase() === lawyer.oab.trim().toUpperCase()
                          : false;
                        return sameName || sameOab;
                      }) || (index === 0 ? publication.matchedPartners?.[0] : null);

                      return (
                        <div key={`${lawyer.nome}-${lawyer.oab || index}`} className="rounded-lg border border-blue-100 bg-blue-50/70 p-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{lawyer.nome}</p>
                              <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-600">
                                {lawyer.oab ? <span>OAB {lawyer.oab}</span> : null}
                                {lawyer.documentos?.[0] ? <span>Documento {lawyer.documentos[0]}</span> : null}
                              </div>
                            </div>
                            {matchedPartner ? (
                              <PartnerLawyerBadge partner={matchedPartner} compact={true} />
                            ) : (
                              <button
                                type="button"
                                onClick={() => setShowLawyerPartnerModal(true)}
                                className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                              >
                                Adicionar como parceiro
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Classifications Section - AGORA EXPANSÍVEL */}
              {classifications && classifications.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <button
                    onClick={() =>
                      setClassificationsExpanded(!classificationsExpanded)
                    }
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-lg">
                        <Hash className="w-4 h-4 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm">
                        Classificações ({classifications.length})
                      </h3>
                    </div>
                    {classificationsExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {classificationsExpanded && (
                    <div className="p-3 space-y-2 border-t border-gray-100">
                      {classifications.map((classification, index) => {
                        const getPriorityColor = (priority) => {
                          switch (priority?.toLowerCase()) {
                            case "urgente":
                              return "bg-red-100 text-red-700 border-red-300";
                            case "alta":
                              return "bg-yellow-100 text-yellow-700 border-yellow-300";
                            case "normal":
                              return "bg-blue-100 text-blue-700 border-blue-300";
                            case "baixa":
                              return "bg-gray-100 text-gray-700 border-gray-300";
                            default:
                              return "bg-gray-100 text-gray-700 border-gray-300";
                          }
                        };

                        return (
                          <div
                            key={index}
                            className={`border rounded-lg p-3 ${getPriorityColor(
                              classification.priority,
                            )}`}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-semibold text-sm">
                                {classification.name}
                              </h4>
                              <span className="text-xs font-medium px-2 py-0.5 rounded">
                                {classification.priority || "Normal"}
                              </span>
                            </div>
                            {classification.reasoning && (
                              <p className="text-xs mt-1 opacity-90">
                                {classification.reasoning}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Monetary Values Section */}
              {monetaryValues && monetaryValues.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 p-3 border-b border-gray-100">
                    <div className="p-1.5 bg-green-100 rounded-lg">
                      <DollarSign className="w-4 h-4 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      Valores Monetários ({monetaryValues.length})
                    </h3>
                  </div>
                  <div className="p-3 space-y-2">
                    {publication.highestValue && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                        <label className="text-xs font-semibold text-green-700 uppercase">
                          Maior Valor
                        </label>
                        <p className="text-xl font-bold text-green-900 mt-1">
                          R${" "}
                          {publication.highestValue.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    )}

                    {monetaryValues.map((value, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded p-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-900">
                            {value.normalizedText || value.originalText}
                          </span>
                          {value.isValid !== undefined && (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                value.isValid
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {value.isValid ? "Válido" : "Inválido"}
                            </span>
                          )}
                        </div>
                        {value.numericValue && (
                          <p className="text-xs text-gray-600 mt-1">
                            Valor numérico: R${" "}
                            {value.numericValue.toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Content Mode - FULLSCREEN OVERLAY only for content area
            <>
              {/* Content fullscreen overlay */}
              <div
                className={`fixed top-0 left-0 w-screen h-screen bg-white z-[10000] flex flex-col transition-all duration-700 ease-out animate-in fade-in slide-in-from-right-4 fullscreen-content`}
              >
                {/* Compact header bar at top with close button */}
                <div className="flex-shrink-0 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-b border-blue-200 p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setViewMode("overview")}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors text-blue-600 hover:text-blue-800"
                        title="Voltar para sidebar"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <h3 className="font-bold text-lg text-blue-900 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Conteúdo Integral •
                        <ProcessNumber
                          processNumber={
                            publication.numeroProcessoFormatted ||
                            publication.numeroProcesso
                          }
                          className="text-sm font-medium text-blue-700"
                          copyable={true}
                        />
                        • 📅{" "}
                        <span className="text-sm font-medium text-blue-700">
                          {publication.dataDisponibilizacao}
                        </span>
                      </h3>
                    </div>

                    {/* Search and typography controls combined */}
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={contentSearchTerm}
                          onChange={(e) => setContentSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && searchResults.length > 0) {
                              e.preventDefault();
                              if (e.shiftKey) {
                                navigateToSearchResult("prev");
                              } else {
                                navigateToSearchResult("next");
                              }
                            }
                          }}
                          placeholder="Buscar no conteúdo..."
                          className="pl-10 pr-16 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white text-gray-900 placeholder-gray-500"
                          style={{ width: "300px" }}
                        />
                        {searchResults.length > 0 && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-blue-700 font-medium bg-blue-100 px-2 py-1 rounded">
                            {currentSearchIndex + 1}/{searchResults.length}
                          </div>
                        )}
                      </div>

                      {searchResults.length > 0 && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigateToSearchResult("prev")}
                            className="p-2 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-800"
                            title="Anterior"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigateToSearchResult("next")}
                            className="p-2 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-800"
                            title="Próximo"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {/* Score badge */}
                      <div className="relative group">
                        <button
                          className={`flex items-center gap-2 px-3 py-2 text-white text-sm rounded-lg transition-colors ${getScoreColor(
                            publication.score,
                          )} border border-opacity-30 shadow-sm cursor-help`}
                          title="Ver detalhes do score"
                        >
                          <Award size={16} />
                          <span className="font-medium">
                            {publication.score || 0}
                          </span>
                        </button>
                      </div>

                      {/* Typography Controls e Copiar */}
                      <div className="flex items-center gap-2 bg-white/80 rounded-lg px-3 py-1.5 border border-blue-200">
                        <span className="text-xs font-medium text-blue-700">
                          Aa
                        </span>
                        <button
                          onClick={() => {
                            const contentEl =
                              document.getElementById("content-text");
                            if (contentEl) {
                              contentEl.className =
                                contentEl.className.replace(/text-\w+/, "") +
                                " text-small";
                            }
                          }}
                          className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                          title="Texto pequeno (12pt)"
                        >
                          A-
                        </button>
                        <button
                          onClick={() => {
                            const contentEl =
                              document.getElementById("content-text");
                            if (contentEl) {
                              contentEl.className =
                                contentEl.className.replace(/text-\w+/, "") +
                                " text-medium";
                            }
                          }}
                          className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                          title="Texto padrão (14pt)"
                        >
                          A
                        </button>
                        <button
                          onClick={() => {
                            const contentEl =
                              document.getElementById("content-text");
                            if (contentEl) {
                              contentEl.className =
                                contentEl.className.replace(/text-\w+/, "") +
                                " text-large";
                            }
                          }}
                          className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                          title="Texto grande (16pt)"
                        >
                          A+
                        </button>

                        {/* Divisor */}
                        <div className="h-6 w-px bg-blue-200"></div>

                        {/* Botão de Copiar */}
                        <button
                          onClick={copyPublicationContent}
                          className="p-2 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                          title="Copiar conteúdo completo da publicação"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fullscreen content area with navigation */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Navigation Sidebar - Always visible and enhanced for fullscreen */}
                  {contentSections.length > 0 && (
                    <div className="sidebar-width flex flex-col bg-white/95 backdrop-blur-sm border-r border-blue-200 shadow-xl z-10 h-full">
                      {/* Scrollable Navigation Area */}
                      <div className="flex-1 overflow-y-auto sidebar-scroll">
                        {/* Classifications Tags - Clickable */}
                        {classifications && classifications.length > 0 && (
                          <div className="border-b border-blue-200 p-3 bg-gradient-to-r from-purple-50 to-indigo-50">
                            <h5 className="font-semibold text-purple-900 mb-3 flex items-center gap-2 text-sm">
                              🏷️ Classificações ({classifications.length})
                            </h5>
                            <div className="flex flex-wrap gap-0.5">
                              {classifications.map((classification, index) => {
                                const getPriorityColor = (priority) => {
                                  switch (priority?.toUpperCase()) {
                                    case "URGENTE":
                                      return "bg-red-100 text-red-800 border-red-300 hover:bg-red-200";
                                    case "ALTA":
                                      return "bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200";
                                    case "NORMAL":
                                      return "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200";
                                    case "BAIXA":
                                      return "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200";
                                    case "IA":
                                      return "bg-black text-white border-black hover:bg-gray-800";
                                    default:
                                      return "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200";
                                  }
                                };

                                return (
                                  <button
                                    key={classification._id || index}
                                    className={`inline-flex items-center justify-center px-1.5 py-1 rounded text-xs font-medium border transition-all duration-200 cursor-pointer transform hover:scale-105 min-w-[60px] text-center ${getPriorityColor(
                                      classification.priority,
                                    )}`}
                                    title={`Prioridade: ${
                                      classification.priority || "Normal"
                                    } • Clique para destacar`}
                                    style={{ fontSize: "8px" }}
                                  >
                                    <span className="truncate">
                                      {classification.name}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Valores Monetários - Seção separada */}
                        {monetaryValues && monetaryValues.length > 0 && (
                          <div className="border-b border-green-200 p-2 bg-gradient-to-r from-green-50 to-emerald-50">
                            <h5 className="font-semibold text-green-900 mb-1.5 flex items-center gap-2 text-xs">
                              💰 Valores Monetários ({monetaryValues.length})
                            </h5>
                            <div className="space-y-1">
                              {monetaryValues.map((value, index) => (
                                <div
                                  key={index}
                                  className="w-full text-left p-1.5 rounded text-xs transition-all border border-green-200 bg-white hover:bg-green-50 text-gray-700"
                                >
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-sm flex-shrink-0">
                                      💰
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-bold text-green-800 font-mono text-xs truncate">
                                        {value.normalizedText ||
                                          value.originalText}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Fim da área scrollable */}
                    </div>
                  )}

                  {/* Content area */}
                  <div
                    className="flex-1 bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 relative"
                    style={{
                      height: "calc(100vh - 140px)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="h-full p-8 zebra-sections content-area content-scroll-container"
                      ref={contentRef}
                    >
                      {contentSearchTerm.trim().length >= 2 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Search className="w-5 h-5 text-blue-600" />
                              <span className="font-medium text-blue-900">
                                {searchResults.length > 0
                                  ? `${searchResults.length} resultado${
                                      searchResults.length !== 1 ? "s" : ""
                                    } encontrado${
                                      searchResults.length !== 1 ? "s" : ""
                                    } para "${contentSearchTerm}"`
                                  : `Nenhum resultado encontrado para "${contentSearchTerm}"`}
                              </span>
                            </div>
                            {searchResults.length > 0 && (
                              <div className="text-sm text-blue-700 bg-blue-100 px-3 py-1 rounded">
                                Atual: {currentSearchIndex + 1}/
                                {searchResults.length}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-blue-200 shadow-xl rounded-lg p-8">
                        <div
                          className="publication-content whitespace-pre-wrap text-gray-900"
                          id="content-text"
                        >
                          <HighlightedContent
                            content={
                              publication.detalhes.conteudo ||
                              publication.texto ||
                              "Conteúdo não disponível para esta publicação."
                            }
                            classifications={classifications}
                            publication={publication}
                            searchTerm={contentSearchTerm}
                            onSearchResultsChange={handleSearchResultsChange}
                            currentSearchIndex={currentSearchIndex}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <QuickLawyerPartnerModal
        open={showLawyerPartnerModal}
        onClose={() => setShowLawyerPartnerModal(false)}
        content={publication?.detalhes?.conteudo || publication?.texto || ''}
        sourceLabel={`publicação DJE ${publication?.numeroProcesso || publication?.id || ''}`}
        initialLawyers={activeLawyerCandidates}
        onCreated={(partner) => {
          setShowLawyerPartnerModal(false);
          onPartnerCreated?.(partner);
        }}
      />

      {/* Modal de Confirmação de Conversão */}
      {showConversionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileCheck className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Converter em Processo
                </h3>
              </div>

              <div className="space-y-4 mb-6">
                <p className="text-gray-700">
                  Deseja converter esta publicação em um processo para análise
                  técnica?
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Processo:
                    </span>
                    <span className="text-sm font-mono text-gray-900">
                      {publication.numeroProcesso}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Score:
                    </span>
                    <span className="text-sm font-semibold text-green-600">
                      {publication.score}/100
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Tribunal:
                    </span>
                    <span className="text-sm text-gray-900">
                      {publication.tribunal}
                    </span>
                  </div>
                  {publication.highestValue && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Valores:
                      </span>
                      <span className="text-sm font-semibold text-green-600">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(publication.highestValue)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    💡 O processo será criado com status "Aguardando Análise" e
                    ficará disponível na tela de gestão de processos.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConversionModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConvertToProcess}
                  disabled={isConverting}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isConverting
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
                >
                  {isConverting ? "Convertendo..." : "Confirmar Conversão"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicationDjeDetailsSidebar;
